import { useMemo, useState, useContext } from "react";
import DOMPurify from "dompurify";
import { Stack } from "@fluentui/react";
import { FaRegThumbsUp, FaRegThumbsDown } from "react-icons/fa";
import styles from "./Answer.module.css"; // Asegúrate que el nombre del archivo CSS coincida
import { AppStateContext } from "../state/AppProvider";
import { AskResponse, getCitationFilePath } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";

interface Props {
    id: number;
    answer: AskResponse;
    isSelected?: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
}

type FeedbackType = "up" | "down" | null;

export const Answer = ({
    id,
    answer,
    isSelected,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions
}: Props) => {
    const appStateContext = useContext(AppStateContext);

    const { answerHtml, citations, followupQuestions } = useMemo(() => {
        const parsed = parseAnswerToHtml(answer.answer, onCitationClicked);
        const safeHtml = DOMPurify.sanitize(parsed.answerHtml);
        return {
            answerHtml: safeHtml,
            citations: parsed.citations,
            followupQuestions: parsed.followupQuestions
        };
    }, [answer.answer, onCitationClicked]);

    const [feedback, setFeedback] = useState<FeedbackType>(null);

    const openLikeModal = () => {
        appStateContext?.dispatch({ type: "TOGGLE_LIKE_MODAL", payload: true });
        appStateContext?.dispatch({ type: "SET_DISIKE_MODAL", payload: false }); // TODO: renombrar acción a SET_DISLIKE_MODAL
        appStateContext?.dispatch({ type: "SET_Message_Selected", payload: id });
    };

    const openDislikeModal = () => {
        appStateContext?.dispatch({ type: "SET_DISIKE_MODAL", payload: true });
        appStateContext?.dispatch({ type: "TOGGLE_LIKE_MODAL", payload: false });
        appStateContext?.dispatch({ type: "SET_Message_Selected", payload: id });
    };

    const closeBothModals = () => {
        appStateContext?.dispatch({ type: "TOGGLE_LIKE_MODAL", payload: false });
        appStateContext?.dispatch({ type: "SET_DISIKE_MODAL", payload: false });
    };

    const handleFeedbackClick = (type: Exclude<FeedbackType, null>) => {
        setFeedback(prev => {
            if (prev === type) {
                // Deseleccionar (cierra modal si lo había)
                closeBothModals();
                return null;
            } else {
                if (type === "up") openLikeModal();
                else openDislikeModal();
                return type;
            }
        });
    };

    return (
        <Stack className={`${styles.answerContainer} ${isSelected ? styles.selected : ""}`} verticalAlign="space-between" tokens={{ childrenGap: 12 }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                <AnswerIcon />
                <div className="messageActions">
                    <button
                        type="button"
                        aria-label="Me gusta esta respuesta"
                        aria-pressed={feedback === "up"}
                        className={`feedbackIcon ${feedback === "up" ? "is-selected" : ""}`}
                        onClick={() => handleFeedbackClick("up")}
                    >
                        <FaRegThumbsUp size={16} />
                    </button>
                    <button
                        type="button"
                        aria-label="No me gusta esta respuesta"
                        aria-pressed={feedback === "down"}
                        className={`feedbackIcon ${feedback === "down" ? "is-selected" : ""}`}
                        onClick={() => handleFeedbackClick("down")}
                    >
                        <FaRegThumbsDown size={16} />
                    </button>
                </div>
            </Stack>

            <div
                className={styles.answerText}
                // El HTML ya viene sanitizado
                dangerouslySetInnerHTML={{ __html: answerHtml }}
            />

            {!!citations.length && (
                <div className={styles.citationsBlock}>
                    <span className={styles.citationLabel}>Citaciones:</span>
                    <div className={styles.citationList}>
                        {citations.map((x, i) => {
                            const path = getCitationFilePath(x);
                            return (
                                <a key={i} className={styles.citation} title={x} onClick={() => onCitationClicked(path)}>
                                    {i + 1}. {x}
                                </a>
                            );
                        })}
                    </div>
                </div>
            )}

            {!!followupQuestions.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <div className={styles.followupBlock}>
                    <span className={styles.followupLabel}>Preguntas sugeridas:</span>
                    <div className={styles.followupList}>
                        {followupQuestions.map((q, i) => (
                            <a key={i} className={styles.followupQuestion} title={q} onClick={() => onFollowupQuestionClicked(q)}>
                                {q}
                            </a>
                        ))}
                    </div>
                </div>
            )}
        </Stack>
    );
};
